import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HomeAnnouncement } from '../components/home-announcement/home-announcement.types';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };
  public hiddenCountries$ = new BehaviorSubject([]);
  // Ops-editable home banner (setting group 'site', name 'homeAnnouncement').
  // null until the settings call resolves, and on any backend that does not
  // return the key yet — the banner simply does not render.
  public homeAnnouncement$ = new BehaviorSubject<HomeAnnouncement | null>(null);

  // Runtime kill switch for the virtuals lobby (setting group 'system', name
  // 'virtualsEnabled' — served as `virtualsEnabled` on /settings/getAllSettings,
  // design §4.6). Emits once per settings load: true only when the backend
  // answered an explicit `true`; false on a missing key, an older backend, or
  // a FAILED call. Deliberately a ReplaySubject with NO initial value: nothing
  // downstream can read "enabled" before the backend has been asked, so the
  // guard and the nav entries fail closed while the call is in flight.
  private readonly virtualsEnabledSubject = new ReplaySubject<boolean>(1);
  public readonly virtualsEnabled$: Observable<boolean> = this.virtualsEnabledSubject
    .asObservable()
    .pipe(distinctUntilChanged());
  private settingsRequested = false;

  constructor(private http: HttpClient) { }

  getGeneralSettings(): Observable<any> {
    this.settingsRequested = true;
    const url = environment.apiUrl + `/settings/getAllSettings`;
    const settingsResponse = this.http.get<any>(url, this.httpOptions);
    settingsResponse.subscribe({
      next: (res) => {
        this.hiddenCountries$.next(res?.hiddenRaceCountries);
        this.homeAnnouncement$.next((res && res.homeAnnouncement) || null);
        this.virtualsEnabledSubject.next(isVirtualsRuntimeOn(res && res.virtualsEnabled));
      },
      error: () => {
        // A settings outage must not open the lobby: report the switch as
        // OFF so ProvidersEnabledGuard / ProvidersVisibilityService resolve
        // instead of waiting forever, and resolve to "hidden".
        this.virtualsEnabledSubject.next(false);
      },
    });
    return settingsResponse;
  }

  /**
   * The first runtime answer for the virtuals switch. Fires the settings
   * load when nobody has yet (a deep link resolved before the layout's own
   * call), so a guard can await it without a second request path.
   */
  virtualsRuntimeEnabledOnce(): Observable<boolean> {
    if (!this.settingsRequested) {
      this.getGeneralSettings();
    }
    return this.virtualsEnabled$.pipe(take(1));
  }

  filterRaceCountries(country): boolean {
    return this.hiddenCountries$.getValue().includes(country);
  }
}

/**
 * The runtime half of the virtuals switch: only an explicit boolean `true`
 * from the settings payload counts. `undefined` (older backend), `null`,
 * `'true'`, `1` — all OFF.
 */
export function isVirtualsRuntimeOn(flag: unknown): boolean {
  return flag === true;
}
